// .../shared/production/refiner-production-editor.tsx

import React, { useMemo } from 'react';
import styled from 'styled-components';
import { BoxedDiv } from 'psims/react/components/layout';
import { H2 } from 'psims/react/components/typography';
import {
  ColumnHeader,
  Table,
  TD,
  TH,
  TR,
} from 'psims/react/pages/primary-pages/data-submissions/shared/data-table-components';
import Input from '../../../shared/input';
import { UseFocusedField } from 'psims/react/util/use-focused-field';
import { FocusField, isDataFocusField, MsoProductView } from '../types';
import { UseRefinerProduction } from './use-refiner-production';
import { UpdateMsoProductionField } from 'psims/models/submission-types/mso/annual-activity/refiner/update-mso-production';

// ── Styled components (mirrors importer pattern) ────────────────────────────

const PSIMSCard = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 16px;
  margin-bottom: 20px;
  border: 1px solid #eee;
`;
const InfoBox = styled.div`
  background: #f5f7fa;
  border: 1px solid #d6dde6;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
  color: #333;
  width: 100%;
  box-sizing: border-box;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.5;
  }
`;
const TableWrapper = styled.div`
  width: 100%;
  table-layout: fixed;
`;

const CompactTableWrapper = styled(TableWrapper)`
  table {
    border-collapse: collapse;
  }
  th {
    padding: 10px;
  }
  td {
    padding: 4px 8px;
  }
  input {
    padding: 2px 6px;
    font-size: 14px;
    height: 28px;
    border: solid 1px silver;
  }
  thead th {
    position: sticky;
    top: 0;
    height: 60px;
    background: #f5f5f5;
    border-top: solid 1px silver;
  }
`;

const ReadOnlyCell = styled.span`
  display: block;
  padding: 2px 6px;
  font-size: 14px;
  min-height: 28px;
  line-height: 28px;
  color: #444;
`;

const TooltipLabel = styled.span`
  font-size: 12px;
  color: #666;
  display: block;
  margin-top: 2px;
`;

// ── Props ────────────────────────────────────────────────────────────────────

interface RefinerProductionEditorProps {
  focusFieldCtrl: UseFocusedField<FocusField>;
  forceErrors: boolean;
  isDisabled: boolean;
  products: Array<MsoProductView>;
  productionCtrl: UseRefinerProduction;
}

// ── Root component ───────────────────────────────────────────────────────────

const RefinerProductionEditor = (props: RefinerProductionEditorProps) => {
  return (
    <PSIMSCard>
      <BoxedDiv box={{ flex: 'column' }}>
        <H2>MSO annual activity</H2>
        <p style={{ padding: '10px 0' }}>
          Fill in the aggregate volumes for refining each MSO product in the
          reporting year. Quantities should be reported to the nearest{' '}
          <strong>megalitre (ML)</strong>.
        </p>

        <CompactTableWrapper>
          <Table caption="Production data" fixed={false}>
            <thead>
              <TR>
                <ColumnHeader label="MSO Product" fixedWidth="450px" />
                <ColumnHeader
                  label="Total annual volume (ML)"
                  Help={
                    <p>
                      The total volume refined for each MSO product during the
                      reporting year. Quantities should be reported to the
                      nearest <strong>megalitre (ML)</strong>.
                    </p>
                  }
                />
                <ColumnHeader
                  label="Indicative MSO based on volumes provided"
                  Help={
                    <div>
                      <p>
                        Calculated as: Total annual volume (ML) &times; (Fuel
                        type target no. of days ÷ 365).
                      </p>
                      <p>This field is read-only.</p>
                    </div>
                  }
                />
              </TR>
            </thead>

            <tbody>
              {props.products.map(product => (
                <ProductRow
                  key={product.id}
                  focusFieldCtrl={props.focusFieldCtrl}
                  forceErrors={props.forceErrors}
                  isDisabled={props.isDisabled}
                  product={product}
                  productionCtrl={props.productionCtrl}
                />
              ))}
            </tbody>
          </Table>
        </CompactTableWrapper>
        
      </BoxedDiv>
    </PSIMSCard>
  );
};

// ── Per-product row ──────────────────────────────────────────────────────────

interface ProductRowProps {
  focusFieldCtrl: UseFocusedField<FocusField>;
  forceErrors: boolean;
  isDisabled: boolean;
  product: MsoProductView;
  productionCtrl: UseRefinerProduction;
}

const ProductRow = ({
  focusFieldCtrl,
  forceErrors,
  isDisabled,
  product,
  productionCtrl,
}: ProductRowProps) => {
  const entry = useMemo(
    () => productionCtrl.productionView.find(v => v.data.msoProductId === product.id),
    [product.id, productionCtrl.productionView]
  );

  const shouldFocus = useMemo(() => {
    return (
      isDataFocusField(focusFieldCtrl.focusedField) &&
      focusFieldCtrl.focusedField._id === entry?.data._id &&
      focusFieldCtrl.focusedField.field === 'totalAnnualVolume'
    );
  }, [entry?.data._id, focusFieldCtrl.focusedField]);

  const updateField = (field: UpdateMsoProductionField, val: number | null) => {
    if (entry == null) return;
    productionCtrl.updateProduction({ ...entry.data, [field]: val });
  };

  if (entry == null) return null;

  return (
    <TR>
      <TH scope="row">{product.name}</TH>

      <TD>
        <Input
          disabled={isDisabled}
          error={entry.validations.totalProductionAnnualVolume ?.inline}
          forceError={forceErrors}
          kind="number"
          label="Total annual volume"
          onChange={val => updateField('totalProductionAnnualVolume', val ?? null)}
          shouldFocus={shouldFocus}
          value={entry.data.totalProductionAnnualVolume  ?? null}
        />
      </TD>

      <TD>
       <Input
            disabled
            kind="number"
            label="Indicative MSO based on volumes provided"
            onChange={() => {}}
            value={entry.indicativeMSO ?? null}
            shouldFocus={false}
          />
          
      </TD>
    </TR>
  );
};

export default RefinerProductionEditor;